import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Ranger L6-10 (manuale V1, rr. 5357-5440). 14 mosse.
 *
 * Pattern notabili:
 * - **`MoveSchema.replaces`** terzo uso strutturale (dopo Mago/FI-21 e
 *   Monaco/2 casi): *Grampasso* sostituisce *Ramingo* (entrambe sulla
 *   mossa "viaggio pericoloso", *Grampasso* aggiunge il terzo ruolo).
 * - **2 mosse richiedono *Marchio del Cacciatore*** (Marchio Multiplo,
 *   Braccare la Preda).
 * - **2 mosse richiedono *Compagno Animale*** (Alleato Innaturale, Abilità
 *   Speciale) — FI-8 stub: prerequisito strutturato anche se il Compagno
 *   è description-only.
 * - **Catene di prerequisiti** L2-5 → L6-10:
 *    - *Seguire Tracce* → *Perspicace*
 *    - *Preda Familiare* → *La Preda del Cacciatore*
 *    - *Bersaglio Facile* → *Tiro Perfetto*
 * - **Mossa multiclasse**: *Addestramento Avanzato* — analogo a *Abilità
 *   Secondarie* di L2-5; description-only (FI-17).
 * - ***Abilità Speciale***: «Questa mossa non si considera multiclasse».
 *   Description-only (FI-17 + FI-8).
 */

const perspicace: Move = {
  id: 'ranger.perspicace',
  name: 'Perspicace',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando *Segui le Tracce* di una creatura',
  description:
    "Con un 7-9, puoi comunque scegliere un'opzione dalla lista del 10+. Con 10+, puoi anche fare al GM una domanda a tua scelta dalla lista di *discernere realtà* riguardo la tua preda.",
  tags: [],
  prerequisites: ['ranger.seguire-tracce'],
  replaces: [],
};

// Sostituisce *Ramingo* via `MoveSchema.replaces`. Il riferimento
// all'archetipo Elfo nella description è coerente con quello di *Ramingo*
// (effetto narrativo, non vincolo runtime).
const grampasso: Move = {
  id: 'ranger.grampasso',
  name: 'Grampasso',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando *intraprendi un viaggio pericoloso*',
  description:
    "Puoi ricoprire tutti e tre i ruoli. Fai un tiro separato per ciascun ruolo. Se hai l'archetipo «*Elfo*» del Ranger, trasforma il peggiore dei tre risultati in un 10+.",
  tags: [],
  prerequisites: [],
  replaces: ['ranger.ramingo'],
};

const marchioMultiplo: Move = {
  id: 'ranger.marchio-multiplo',
  name: 'Marchio Multiplo',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Puoi mantenere *Marchio del Cacciatore* contemporaneamente attivo su due creature. Se ne marchi una terza, scegli quale dei primi due marchi cancellare senza che si attivino gli effetti della rimozione.',
  tags: [],
  prerequisites: ['ranger.marchio-del-cacciatore'],
  replaces: [],
};

const braccareLaPreda: Move = {
  id: 'ranger.braccare-la-preda',
  name: 'Braccare la Preda',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger:
    'Quando fai *taglia e spacca* contro una creatura marchiata con *Marchio del Cacciatore*',
  description:
    'Con 10+, puoi decidere di incalzarla, spingendola in una posizione a te più vantaggiosa. Se lo fai, non infliggi alcun danno, ma puoi applicare qualsiasi altro effetto del tuo attacco che non sia legato al danno. Con 12+, la incalzi, infliggi il tuo Danno e puoi applicare anche gli effetti legati al danno.',
  tags: [],
  prerequisites: ['ranger.marchio-del-cacciatore'],
  replaces: [],
};

const alleatoInnaturale: Move = {
  id: 'ranger.alleato-innaturale',
  name: 'Alleato Innaturale',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up (upgrade del Compagno Animale)',
  description:
    'Il tuo *Compagno Animale* si rivela essere un mostro, non un semplice animale. Il modificatore di Caratteristica che aveva a +2 diventa +3, il suo livello da gregario sale a 3, e scegli due nuove capacità utili dalla lista di *Compagno Animale*.',
  tags: [],
  prerequisites: ['ranger.compagno-animale'],
  replaces: [],
  // D8c — FI-41: upgrade del Compagno Animale, level/mod statici + 2 capacità
  // custom sempre presenti. Il template `ranger-compagno-animale` dichiara
  // `levelOverride: 3` + `modifierBonusOverride: amount 3` +
  // `additionalAlwaysTraitsFromNarrativeFields` che mappa qui.
  narrativeFields: [
    {
      id: 'descrizione-metamorfosi',
      label: 'Descrizione della metamorfosi del Compagno',
      placeholder:
        'In che modo il Compagno si rivela essere un mostro? Forma vera, segni, comportamenti…',
      required: true,
      multiline: true,
    },
    {
      id: 'ai-cap1-label',
      label: '1ª capacità utile — titolo',
      placeholder: 'es. Sguardo mortale, Forma di mostro, Aura di terrore…',
      required: true,
      multiline: false,
    },
    {
      id: 'ai-cap1-desc',
      label: '1ª capacità utile — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
    },
    {
      id: 'ai-cap2-label',
      label: '2ª capacità utile — titolo',
      placeholder: 'es. Pelle squamosa, Zanne velenose, Volo planato…',
      required: true,
      multiline: false,
    },
    {
      id: 'ai-cap2-desc',
      label: '2ª capacità utile — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
    },
  ],
};

// Pattern composto description-only: multiclasse cross-class (FI-17) +
// Compagno Animale (FI-8). L'attivazione condizionata «mentre collaborate»
// resta narrativa fino al sistema Seguaci strutturato.
const abilitaSpeciale: Move = {
  id: 'ranger.abilita-speciale',
  name: 'Abilità Speciale',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    "Scegli una mossa di un'altra classe. Fintanto che **tu e il tuo *Compagno Animale* collaborate**, puoi usare quella mossa, ma non benefici del consueto +1 al tiro.\n\nQuesta mossa *non si considera* una mossa multiclasse.",
  tags: [],
  prerequisites: ['ranger.compagno-animale'],
  replaces: [],
};

const zanneDellaVipera: Move = {
  id: 'ranger.zanne-della-vipera',
  name: 'Zanne della Vipera',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando attacchi un avversario in mischia usando due armi',
  description: 'Se infliggi danno, aggiungi +1d4 danni grazie alla tua seconda arma.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const laPredaDelCacciatore: Move = {
  id: 'ranger.la-preda-del-cacciatore',
  name: 'La Preda del Cacciatore',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando *declami conoscenze* su di un mostro',
  description:
    "Con 10+, oltre a ricevere un'informazione utile e dettagliata, puoi anche fare al GM una domanda qualsiasi su tale creatura e ricevere una risposta sincera.",
  tags: [],
  prerequisites: ['ranger.preda-familiare'],
  replaces: [],
};

const passoNellOmbra: Move = {
  id: 'ranger.passo-nell-ombra',
  name: "Passo nell'Ombra",
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: "Quando entri in un'area oscura e piena di ombre",
  description:
    "Puoi uscire da un qualunque altro luogo oscuro e pieno di ombre che si trovi entro gittata *vicino*. Per spostarti devi attraversare l'ombra completamente, con tutto il tuo corpo e con tutto ciò che porti.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const tiroPerfetto: Move = {
  id: 'ranger.tiro-perfetto',
  name: 'Tiro Perfetto',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando fai una *raffica*',
  description:
    'Con 10+, infliggi il tuo Danno e scegli uno. Con 12+, ottieni entrambi:\n\n- infliggi +1d4 danni;\n- applichi un effetto della lista di *Bersaglio Facile*.',
  tags: [],
  prerequisites: ['ranger.bersaglio-facile'],
  replaces: [],
};

const fuocoDiCopertura: Move = {
  id: 'ranger.fuoco-di-copertura',
  name: 'Fuoco di Copertura',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: "Quando usi un'arma a distanza per coprire le spalle a un tuo alleato",
  description:
    "Tira+**DES**. Con 10+, prendi 2. Con 7-9, prendi 1. Se il tuo alleato attacca o viene attaccato da un nemico, spendi 1 presa per scegliere un'opzione dalla lista:\n\n- infliggi il tuo dado di Danno all'avversario (solo il dado, senza applicare altri bonus o etichette);\n- dimezzi i danni (per eccesso) o gli effetti dell'attacco nemico;\n- crei un varco nella difesa del nemico e l'alleato prende +1 al prossimo tiro contro di esso.\n\nPuoi spendere più prese per volta, ma devi scegliere opzioni diverse. Se fai qualcosa che non sia coprire l'alleato, perdi tutte le prese.",
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Prendi 2.' },
    on7to9: { text: 'Prendi 1.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const addestramentoAvanzato: Move = {
  id: 'ranger.addestramento-avanzato',
  name: 'Addestramento Avanzato',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa del Barbaro, del Druido, del Guerriero, del Ladro o dello Sciamano. Non scegliere mosse multiclasse.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool ristretto a 5 classi (simmetrico a *Abilità Secondarie*).
  multiclass: {
    picks: {
      kind: 'fixed-classes',
      classes: ['barbaro', 'druido', 'guerriero', 'ladro', 'sciamano'],
    },
  },
};

const ilVentreDiSamug: Move = {
  id: 'ranger.il-ventre-di-samug',
  name: 'Il Ventre di Samug',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger:
    'Quando conosci un punto debole di un avversario che non saresti altrimenti in grado di ferire',
  description:
    'Descrivi come intendi colpirlo e, se ciò è anche solo minimamente plausibile, puoi fare un attacco contro il tuo bersaglio senza dover usare altre mosse per crearti la giusta opportunità. Armatura e resistenze al danno del nemico, però, restano invariate e dovrai riuscire a superarle per ferirlo davvero.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const studiareLaPreda: Move = {
  id: 'ranger.studiare-la-preda',
  name: 'Studiare la Preda',
  type: 'advanced_6_10',
  classId: 'ranger',
  trigger:
    'Quando *discerni la realtà* riguardo qualcuno con cui hai avuto modo di parlare per un certo tempo',
  description:
    'Aggiungi la domanda «*Qual è la cosa di cui ha più paura?*» a quelle che puoi fare. Puoi usare ciò che hai scoperto come leva per *parlamentare*.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const rangerAdvanced6to10Moves: readonly Move[] = [
  perspicace,
  grampasso,
  marchioMultiplo,
  braccareLaPreda,
  alleatoInnaturale,
  abilitaSpeciale,
  zanneDellaVipera,
  laPredaDelCacciatore,
  passoNellOmbra,
  tiroPerfetto,
  fuocoDiCopertura,
  addestramentoAvanzato,
  ilVentreDiSamug,
  studiareLaPreda,
] as const;
